import styled from "styled-components/native";
import OptionHeader from "../OptionHeader";
import { ProductCategory } from "@utils/constants";
import { DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { SpaceType } from "@components/atoms/Spacer/types";
import { CheckBox, Spacer } from "@components/atoms";
import DropDown from "../DropDown";
import RadioButton from "@components/atoms/RadioButton";
import { useEffect, useState } from "react";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { DefaultTheme } from "styled-components/native";

type JewelryBuilderMultiSelectCheckBoxWithDropDownProps = {
  productType: ProductCategory;
  configObject: DisplayConfigurationV2;
  fromReviewPage?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  fromOrderCart?: boolean;
  fromRequest?: boolean;
  isRadioButton?: boolean;
};

const JewelryBuilderMultiSelectCheckBoxWithDropDown = ({
  productType,
  configObject,
  isRadioButton = false,
}: JewelryBuilderMultiSelectCheckBoxWithDropDownProps) => {
  const { theme } = useScreenOptionsHooks();
  const { addToOption, jcSelectedOption } = useJewelryConfiguratorState();

  const [selectedItem, setSelectedItem] = useState<string[]>([]);
  const [dropDownSelectedItem, setDropDownSelectedItem] = useState<string>("");
  const [selectedSubOption, setSelectedSubOption] = useState({});

  useEffect(() => {
    if (
      configObject?.selected_item?.selected_slugs ??
      jcSelectedOption.current.option_value[configObject.option_type]
        ?.selected_slugs
    ) {
      setSelectedItem(
        configObject?.selected_item?.selected_slugs ??
          jcSelectedOption.current.option_value[configObject.option_type]
            .selected_slugs
      );
    }
    if (
      configObject?.selected_item?.sub_options ||
      jcSelectedOption.current.option_value[configObject.option_type]
        ?.sub_options
    ) {
      const value =
        configObject?.selected_item ||
        jcSelectedOption.current.option_value[configObject.option_type];
      const extracted = value?.selected_slugs
        ?.map(
          (slug: string) =>
            value?.sub_options?.[slug] || value?.sub_options?.[value]
        )
        .filter(Boolean)
        .flat();
      if (extracted?.length > 0) {
        setDropDownSelectedItem(extracted[0]);
      }
      setSelectedSubOption(
        configObject?.selected_item?.sub_options ||
          jcSelectedOption.current.option_value[configObject.option_type]
            ?.sub_options
      );
    }
  }, [
    configObject?.selected_item,
    jcSelectedOption.current.option_value[configObject.option_type],
  ]);

  return (
    <ContainerView>
      <HeaderContainer>
        <OptionHeader
          title={configObject.option_name}
          productType={productType}
          helperText={configObject.option_helper_text}
          disableToggleButton={configObject?.disable_modify_variation ?? false}
        />
      </HeaderContainer>
      {configObject.option_value?.map((item) => (
        <ItemContainer>
          {isRadioButton ? (
            <RadioButton
              checked={
                selectedItem?.includes(item?.value) ||
                selectedItem?.includes(item?.slug)
              }
              label={item.label}
              onPress={() => {
                if (
                  selectedItem?.includes(item?.value) ||
                  selectedItem?.includes(item?.slug)
                ) {
                  setSelectedItem((prev) =>
                    prev.filter((i) => i !== item?.slug || i !== item?.value)
                  );
                  if (item?.has_sub_options) {
                    setSelectedSubOption({});
                  }
                } else {
                  setSelectedItem([...selectedItem, item?.value || item?.slug]);
                }
                addToOption(configObject?.option_type, [
                  configObject,
                  {
                    selected_slugs: [item?.value || item?.slug],
                    sub_options: {},
                  },
                ]);
              }}
              margin="0"
              labelColor={
                selectedItem?.includes(item?.value) ||
                selectedItem?.includes(item?.slug)
                  ? theme["core-05"]
                  : theme["core-06"]
              }
            />
          ) : (
            <CheckBox
              text={item.label}
              isChecked={
                selectedItem?.includes(item?.value) ||
                selectedItem?.includes(item?.slug)
              }
              onPress={() => {
                let subOptions = selectedSubOption;
                if (
                  selectedItem?.includes(item?.value) ||
                  selectedItem?.includes(item?.slug)
                ) {
                  setSelectedItem((prev) => {
                    const value = item?.value || item?.slug;
                    if (prev.includes(value)) {
                      return prev.filter((i) => i !== value);
                    } else {
                      return [...prev, value];
                    }
                  });
                  if (item?.has_sub_options) {
                    setSelectedSubOption({});
                    setDropDownSelectedItem("");
                    subOptions = {};
                  }
                } else {
                  setSelectedItem([...selectedItem, item?.value || item?.slug]);
                }
                addToOption(configObject?.option_type, [
                  configObject,
                  {
                    selected_slugs: [item?.value || item?.slug],
                    sub_options: subOptions,
                  },
                ]);
              }}
              checkBoxSize={20}
              labelColor={
                selectedItem?.includes(item?.value) ||
                selectedItem?.includes(item?.slug)
                  ? theme["core-05"]
                  : theme["core-06"]
              }
            />
          )}
          {item?.has_sub_options &&
            item?.sub_options?.length > 0 &&
            (selectedItem?.includes(item?.value) ||
              selectedItem?.includes(item?.slug)) && (
              <Container>
                <DropDown
                  defaultValue={dropDownSelectedItem}
                  options={item.sub_options}
                  productType={productType}
                  isSubOptions={item?.has_sub_options}
                  onSelect={(_value) => {
                    setDropDownSelectedItem(_value);
                    setSelectedSubOption({ [item?.value]: [_value] });
                    addToOption(configObject?.option_type, [
                      configObject,
                      {
                        selected_slugs: [],
                        sub_options: { [item?.value]: [_value] },
                      },
                    ]);
                  }}
                />
              </Container>
            )}
          <Spacer size={SpaceType.ExtraSmall} />
        </ItemContainer>
      ))}
    </ContainerView>
  );
};

export default JewelryBuilderMultiSelectCheckBoxWithDropDown;

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
`;

const Container = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-right: ${SpaceType.Double}px;
  width: 100%;
  gap: 12px;
`;

const HeaderContainer = styled.View`
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Basic}px;
`;

const ItemContainer = styled.View`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${SpaceType.Double}px;
  border-top-width: 1px;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;
